import { useCallback, useMemo, useState, useSyncExternalStore, type CSSProperties, type DragEvent as ReactDragEvent } from "react";

import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { CardTitleWithHelp, HoverHintContent } from "@/components/ui/title-help";
import { cn } from "@/lib/utils";
import { stripAltTlaSkuPrefixForDisplay } from "@/lib/mrp";

import { formatTlaPlanningDate } from "./tlaPlanningShared";

export const KODIAK_PREFIX_DRAG_MIME = "application/x-kodiak-pcba-prefix";

export interface KodiakPrefixDragPayload {
  purchase_order_id: number | null;
  pcba_prefix: string;
  core_feature: string;
  from_pcba_build_order_id: number | null;
}

export interface KodiakDiscoveredPrefix {
  prefix: string;
  serial_fci_count: number;
  qc_incident_count: number;
  serial_begin_date: string | null;
  serial_end_date: string | null;
  core_feature: string | null;
}

export interface KodiakPcbaPrefixTarget {
  purchase_order_id: number;
  po_number: string;
  pcba_build_order_id: number;
  pcba_work_order_number: string;
  pcba_sku: string;
  core_feature: string | null;
}

export interface KodiakPcbaPrefixAssignment {
  purchase_order_id: number;
  po_number: string;
  pcba_prefix: string;
  core_feature: string;
  pcba_build_order_id: number;
  pcba_work_order_number: string;
}

export interface KodiakPlannedPrefix {
  pcba_prefix: string;
  core_feature: string;
  expected_tla_sku: string;
  purchase_order_id: number | null;
  pcba_build_order_id: number | null;
  tla_build_order_id: number | null;
  tla_work_order_number: string | null;
  po_number: string | null;
  notes: string | null;
  allowed_tla_skus: string[] | null;
}

export interface KodiakPrefixAssignmentBoard {
  discovered_prefixes: KodiakDiscoveredPrefix[];
  discovered_prefix_hover: KodiakDiscoveredPrefixHoverDetail[];
  planned_prefixes: KodiakPlannedPrefix[];
  pcba_targets: KodiakPcbaPrefixTarget[];
  assignments: KodiakPcbaPrefixAssignment[];
  assignment_hover: KodiakPrefixAssignmentHoverDetail[];
}

export interface KodiakDiscoveredPrefixHoverDetail {
  pcba_prefix: string;
  core_feature: string;
  rows: KodiakPrefixAssignmentHoverRow[];
}

export interface KodiakPrefixAssignmentHoverDetail {
  purchase_order_id: number;
  pcba_build_order_id: number;
  pcba_prefix: string;
  rows: KodiakPrefixAssignmentHoverRow[];
  rewire_note?: string | null;
}

export interface KodiakPrefixAssignmentHoverRow {
  pcba_sku: string;
  tla_sku: string;
  serial_fci_count: number;
  qc_incident_count: number;
  serial_begin_date: string | null;
  serial_end_date: string | null;
}

const KODIAK_PREFIX_HOVER_HEADERS = [
  "PCBA SKU",
  "TLA SKU",
  "qty serial_fci",
  "qty qc_incident",
  "start date",
  "stop date",
] as const;

export interface KodiakPrefixAssignmentViewModel {
  discovered_by_variant: Map<string, KodiakDiscoveredPrefix>;
  discovered_hover_by_variant: Map<string, KodiakPrefixAssignmentHoverRow[]>;
  planned_by_variant: Map<string, KodiakPlannedPrefix>;
  unallocated_prefixes: KodiakDiscoveredPrefix[];
  chips_by_build_order_id: Map<number, KodiakPcbaPrefixAssignment[]>;
  hover_by_assignment_key: Map<string, KodiakPrefixAssignmentHoverDetail>;
  core_feature_by_variant: Map<string, string | null>;
  pcba_core_feature_by_build_order_id: Map<number, string | null>;
}

export type KodiakPrefixVariantCore = "N" | "B" | "Q" | "?";

export function kodiakPrefixVariantKey(prefix: string, coreFeature: string): string {
  return `${prefix}:${coreFeature}`;
}

export function kodiakPrefixVariantLabel(prefix: string, coreFeature: string): string {
  return `${prefix}:${coreFeature}`;
}

export type KodiakPcbaCoreFeature = "N" | "Q" | "B" | "W";

let activeKodiakPrefixDragPayload: KodiakPrefixDragPayload | null = null;
const activeKodiakPrefixDragListeners = new Set<() => void>();

export function setActiveKodiakPrefixDragPayload(payload: KodiakPrefixDragPayload | null): void {
  activeKodiakPrefixDragPayload = payload;
  for (const listener of activeKodiakPrefixDragListeners) {
    listener();
  }
}

export function parseKodiakPrefixDragPayloadFromDropEvent(
  event: ReactDragEvent,
): KodiakPrefixDragPayload | null {
  const raw = event.dataTransfer.getData(KODIAK_PREFIX_DRAG_MIME);
  if (raw !== "") {
    try {
      return JSON.parse(raw) as KodiakPrefixDragPayload;
    } catch {
      /* fall through to module drag payload */
    }
  }
  return getActiveKodiakPrefixDragPayload();
}

export function clearActiveKodiakPrefixDragPayloadDeferred(): void {
  window.setTimeout(() => {
    setActiveKodiakPrefixDragPayload(null);
  }, 0);
}

export function getActiveKodiakPrefixDragPayload(): KodiakPrefixDragPayload | null {
  return activeKodiakPrefixDragPayload;
}

function subscribeActiveKodiakPrefixDrag(listener: () => void): () => void {
  activeKodiakPrefixDragListeners.add(listener);
  return () => {
    activeKodiakPrefixDragListeners.delete(listener);
  };
}

export function useActiveKodiakPrefixDragPayload(): KodiakPrefixDragPayload | null {
  return useSyncExternalStore(
    subscribeActiveKodiakPrefixDrag,
    getActiveKodiakPrefixDragPayload,
    () => null,
  );
}

export function kodiakPcbaSkuCoreFeature(pcbaSku: string): KodiakPcbaCoreFeature | null {
  const trimmed = pcbaSku.trim();
  const fi3cMatch = trimmed.match(/^15-1607-([NBQ])-/i);
  const fi3cFeature = fi3cMatch?.[1]?.toUpperCase();
  if (fi3cFeature === "N" || fi3cFeature === "Q" || fi3cFeature === "B") {
    return fi3cFeature;
  }
  if (/^15-1485-W-/i.test(trimmed)) {
    return "W";
  }
  return null;
}

export function kodiakTlaSkuCoreFeature(tlaSku: string): KodiakPcbaCoreFeature | null {
  const normalized = tlaSku.replace(/^ALT-/, "").replace(/^BABA-/, "").trim();
  if (/FI-5A-W-TLA/i.test(normalized)) {
    return "W";
  }
  const match = normalized.match(/-([NBQ])-TLA-/i);
  const feature = match?.[1]?.toUpperCase();
  return feature === "N" || feature === "Q" || feature === "B" ? feature : null;
}

export function resolveKodiakPrefixCoreFeature(
  prefix: string,
  coreFeature: string,
  payload: KodiakPrefixDragPayload,
  viewModel: KodiakPrefixAssignmentViewModel,
): string | null {
  const payloadCore = payload.core_feature.trim().toUpperCase();
  if (payloadCore !== "") return payloadCore;
  const variantCore = viewModel.core_feature_by_variant.get(kodiakPrefixVariantKey(prefix, coreFeature));
  if (variantCore != null) return variantCore;
  if (payload.from_pcba_build_order_id != null) {
    const fromCore = viewModel.pcba_core_feature_by_build_order_id.get(
      Number(payload.from_pcba_build_order_id),
    );
    if (fromCore != null) return fromCore;
  }
  return coreFeature;
}

export type KodiakPrefixChipFamily =
  | "3c-n"
  | "3c-b"
  | "3c-q"
  | "dm-1"
  | "5a-n"
  | "5a-w"
  | "unknown";

export type KodiakPrefixTlaVariant = "A" | "C";

export interface KodiakPrefixChipAppearance {
  family: KodiakPrefixChipFamily;
  tlaVariant: KodiakPrefixTlaVariant | null;
  className: string;
  style: CSSProperties;
  misallocated?: boolean;
}

const KODIAK_PREFIX_CHIP_PALETTE: Record<
  Exclude<KodiakPrefixChipFamily, "unknown">,
  {
    base: string;
    aEnd: string;
    cEnd: string;
    text: string;
    border: string;
  }
> = {
  "3c-n": {
    base: "#dbeafe",
    aEnd: "#60a5fa",
    cEnd: "#38bdf8",
    text: "#1e3a8a",
    border: "#93c5fd",
  },
  "3c-b": {
    base: "#fef3c7",
    aEnd: "#f59e0b",
    cEnd: "#fbbf24",
    text: "#92400e",
    border: "#fcd34d",
  },
  "3c-q": {
    base: "#ede9fe",
    aEnd: "#8b5cf6",
    cEnd: "#c084fc",
    text: "#5b21b6",
    border: "#c4b5fd",
  },
  "dm-1": {
    base: "#d1fae5",
    aEnd: "#34d399",
    cEnd: "#34d399",
    text: "#065f46",
    border: "#6ee7b7",
  },
  "5a-n": {
    base: "#ffe4e6",
    aEnd: "#fb7185",
    cEnd: "#fb7185",
    text: "#9f1239",
    border: "#fda4af",
  },
  "5a-w": {
    base: "#cffafe",
    aEnd: "#22d3ee",
    cEnd: "#22d3ee",
    text: "#155e75",
    border: "#67e8f9",
  },
};

export function parseKodiakPrefixTlaVariant(tlaSku: string): KodiakPrefixTlaVariant | null {
  const normalized = tlaSku.replace(/^ALT-/, "").replace(/^BABA-/, "").trim();
  const shortMatch = normalized.match(/^([NBQ])-TLA-([AC])$/i);
  if (shortMatch) {
    return shortMatch[2]?.toUpperCase() === "C" ? "C" : "A";
  }
  const longMatch = normalized.match(/-([NBQ])-TLA-([AC])\b/i);
  if (longMatch) {
    return longMatch[2]?.toUpperCase() === "C" ? "C" : "A";
  }
  return null;
}

function resolveKodiakPrefixChipFamilyFromSkuHints(
  tlaSku: string | null | undefined,
  pcbaSku: string | null | undefined,
): KodiakPrefixChipFamily | null {
  const normalizedTla = (tlaSku ?? "").replace(/^ALT-/, "").replace(/^BABA-/, "").trim();
  if (normalizedTla !== "" && normalizedTla !== "—") {
    if (/DM-1/i.test(normalizedTla)) return "dm-1";
    if (/FI-5A-W/i.test(normalizedTla)) return "5a-w";
    if (/FI-5A-N/i.test(normalizedTla)) return "5a-n";
    const shortCore = normalizedTla.match(/^([NBQ])-TLA-/i)?.[1];
    if (shortCore === "N" || shortCore === "B" || shortCore === "Q") {
      return `3c-${shortCore.toLowerCase()}` as KodiakPrefixChipFamily;
    }
    const longCore = normalizedTla.match(/-([NBQ])-TLA-/i)?.[1];
    if (longCore === "N" || longCore === "B" || longCore === "Q") {
      return `3c-${longCore.toLowerCase()}` as KodiakPrefixChipFamily;
    }
  }

  const normalizedPcba = (pcbaSku ?? "").trim();
  if (normalizedPcba !== "" && normalizedPcba !== "—") {
    if (/^15-1609/i.test(normalizedPcba)) return "dm-1";
    if (/^15-1485-W-/i.test(normalizedPcba)) return "5a-w";
    const pcbaCore = normalizedPcba.match(/^15-1607-([NBQ])-/i)?.[1];
    if (pcbaCore === "N" || pcbaCore === "B" || pcbaCore === "Q") {
      return `3c-${pcbaCore.toLowerCase()}` as KodiakPrefixChipFamily;
    }
  }

  return null;
}

function resolveKodiakPrefixChipFamilyFromCoreFeature(
  coreFeature: string | null | undefined,
): KodiakPrefixChipFamily | null {
  if (coreFeature === "W") {
    return "5a-w";
  }
  if (coreFeature === "N" || coreFeature === "B" || coreFeature === "Q") {
    return `3c-${coreFeature.toLowerCase()}` as KodiakPrefixChipFamily;
  }
  return null;
}

function weightedPrefixHoverRows(
  rows: KodiakPrefixAssignmentHoverRow[],
): KodiakPrefixAssignmentHoverRow[] {
  return rows.filter((row) => row.serial_fci_count > 0 || row.qc_incident_count > 0);
}

function buildKodiakSkuChipAppearance(
  family: KodiakPrefixChipFamily,
  tlaVariant: KodiakPrefixTlaVariant | null,
): KodiakPrefixChipAppearance {
  if (family === "unknown") {
    return {
      family,
      tlaVariant: null,
      className: "border-slate-200 bg-white text-slate-800",
      style: {},
    };
  }

  const palette = KODIAK_PREFIX_CHIP_PALETTE[family];
  const useGradient = family.startsWith("3c-") && tlaVariant != null;
  const gradientEnd = tlaVariant === "C" ? palette.cEnd : palette.aEnd;

  return {
    family,
    tlaVariant,
    className: "shadow-sm",
    style: {
      color: palette.text,
      borderColor: palette.border,
      ...(useGradient
        ? { background: `linear-gradient(135deg, ${palette.base} 0%, ${gradientEnd} 100%)` }
        : { backgroundColor: palette.base }),
    },
  };
}

export function resolveKodiakTlaSkuChipAppearance(
  tlaSku: string | null | undefined,
  pcbaSku?: string | null | undefined,
): KodiakPrefixChipAppearance {
  const normalizedTla = (tlaSku ?? "").trim();
  if (normalizedTla === "" || normalizedTla === "—") {
    return buildKodiakSkuChipAppearance("unknown", null);
  }

  const family =
    resolveKodiakPrefixChipFamilyFromSkuHints(normalizedTla, pcbaSku) ??
    resolveKodiakPrefixChipFamilyFromCoreFeature(kodiakPcbaSkuCoreFeature(pcbaSku ?? "")) ??
    "unknown";
  const tlaVariant = family.startsWith("3c-") ? parseKodiakPrefixTlaVariant(normalizedTla) : null;
  return buildKodiakSkuChipAppearance(family, tlaVariant);
}

export function KodiakFamilyChip({
  label,
  appearance,
  className,
}: {
  label: string;
  appearance: KodiakPrefixChipAppearance;
  className?: string;
}): JSX.Element {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-2 py-0.5 font-mono text-xs",
        appearance.className,
        className,
      )}
      style={appearance.style}
    >
      {label}
    </span>
  );
}

export function resolveKodiakPrefixChipAppearance(
  discovered: KodiakDiscoveredPrefix | undefined,
  hoverDetail: KodiakPrefixAssignmentHoverDetail | undefined,
): KodiakPrefixChipAppearance {
  const hoverRows = weightedPrefixHoverRows(hoverDetail?.rows ?? []);
  const familyWeights = new Map<KodiakPrefixChipFamily, number>();
  const variantWeights = new Map<KodiakPrefixTlaVariant, number>();

  for (const row of hoverRows) {
    const family =
      resolveKodiakPrefixChipFamilyFromSkuHints(row.tla_sku, row.pcba_sku) ??
      resolveKodiakPrefixChipFamilyFromCoreFeature(discovered?.core_feature);
    if (family == null) continue;
    const weight = row.serial_fci_count + row.qc_incident_count;
    familyWeights.set(family, (familyWeights.get(family) ?? 0) + weight);
    const variant = parseKodiakPrefixTlaVariant(row.tla_sku);
    if (variant != null && family.startsWith("3c-")) {
      variantWeights.set(variant, (variantWeights.get(variant) ?? 0) + weight);
    }
  }

  const family =
    [...familyWeights.entries()].sort((left, right) => right[1] - left[1])[0]?.[0] ??
    resolveKodiakPrefixChipFamilyFromCoreFeature(discovered?.core_feature) ??
    "unknown";

  const tlaVariant: KodiakPrefixTlaVariant | null = family.startsWith("3c-")
    ? ([...variantWeights.entries()].sort((left, right) => right[1] - left[1])[0]?.[0] ?? null)
    : null;

  return buildKodiakSkuChipAppearance(family, tlaVariant);
}

export function resolveKodiakPrefixDiscoveredDominantTlaShortLabel(
  hoverDetail: KodiakPrefixAssignmentHoverDetail | undefined,
): string | null {
  const hoverRows = weightedPrefixHoverRows(hoverDetail?.rows ?? []);
  const tlaWeights = new Map<string, number>();
  for (const row of hoverRows) {
    const short = kodiakTlaSkuShortLabel(row.tla_sku);
    if (short === "" || short === "—" || short.endsWith("-TLA-?")) {
      continue;
    }
    const weight = row.serial_fci_count + row.qc_incident_count;
    tlaWeights.set(short, (tlaWeights.get(short) ?? 0) + weight);
  }
  return [...tlaWeights.entries()].sort((left, right) => right[1] - left[1])[0]?.[0] ?? null;
}

function kodiakHoverRowHasMaterial(row: KodiakPrefixAssignmentHoverRow): boolean {
  return row.serial_fci_count > 0 || row.qc_incident_count > 0;
}

function kodiakResolveHoverRowCoreFeature(
  row: KodiakPrefixAssignmentHoverRow,
): KodiakPcbaCoreFeature | null {
  return kodiakTlaSkuCoreFeature(row.tla_sku) ?? kodiakPcbaSkuCoreFeature(row.pcba_sku);
}

function kodiakHoverRowIsUnallocatedTla(row: KodiakPrefixAssignmentHoverRow): boolean {
  return row.tla_sku.trim().endsWith("-TLA-?");
}

/** PCBA row: wrong N/Q/B core feature in any material row; A vs C mix is allowed. */
function kodiakPcbaLevelPrefixMisallocated(
  hoverRows: KodiakPrefixAssignmentHoverRow[],
  planned: KodiakPlannedPrefix,
): boolean {
  const assignedCore = planned.core_feature.trim().toUpperCase();
  if (assignedCore === "?") {
    return false;
  }
  return hoverRows.some((row) => {
    if (!kodiakHoverRowHasMaterial(row)) {
      return false;
    }
    const rowCore = kodiakResolveHoverRowCoreFeature(row);
    return rowCore != null && rowCore !== assignedCore;
  });
}

/** TLA sub-row: any material row must match the locked TLA (variant and core feature). */
function kodiakTlaLevelPrefixMisallocated(
  hoverRows: KodiakPrefixAssignmentHoverRow[],
  planned: KodiakPlannedPrefix,
): boolean {
  const assignedCore = planned.core_feature.trim().toUpperCase();
  const expectedTla = planned.expected_tla_sku;
  return hoverRows.some((row) => {
    if (!kodiakHoverRowHasMaterial(row)) {
      return false;
    }
    if (kodiakHoverRowIsUnallocatedTla(row)) {
      return true;
    }
    const rowCore = kodiakResolveHoverRowCoreFeature(row);
    if (assignedCore !== "?" && rowCore != null && rowCore !== assignedCore) {
      return true;
    }
    return !kodiakTlaSkusEquivalent(row.tla_sku, expectedTla);
  });
}

/** True when serial/QC discovery disagrees with the assignment lock at PCBA or TLA level. */
export function resolveKodiakPrefixChipMisallocated(
  hoverDetail: KodiakPrefixAssignmentHoverDetail | undefined,
  planned: KodiakPlannedPrefix | null | undefined,
): boolean {
  if (planned == null) {
    return false;
  }
  const hoverRows = weightedPrefixHoverRows(hoverDetail?.rows ?? []);
  if (hoverRows.length === 0) {
    return false;
  }

  if (kodiakPlannedLockIsTlaSpecific(planned)) {
    return kodiakTlaLevelPrefixMisallocated(hoverRows, planned);
  }
  return kodiakPcbaLevelPrefixMisallocated(hoverRows, planned);
}

export function resolveKodiakPrefixChipAppearanceFromDiscovery(
  discovered: KodiakDiscoveredPrefix | undefined,
  discoveryHover: KodiakPrefixAssignmentHoverDetail | undefined,
  planned: KodiakPlannedPrefix | null | undefined,
): KodiakPrefixChipAppearance {
  const appearance = resolveKodiakPrefixChipAppearance(discovered, discoveryHover);
  const misallocated = resolveKodiakPrefixChipMisallocated(discoveryHover, planned);
  if (!misallocated) {
    return appearance;
  }
  return {
    ...appearance,
    misallocated: true,
    className: cn(appearance.className, "ring-2 ring-red-500 ring-offset-1"),
  };
}

/**
 * FI-3C keeps the clamp letter (N-TLA-A). FI-5A has no clamp designator (N-TLA, W-TLA).
 * BABA/ABB prefixes are retained so converted TLAs do not equate to the base SKU.
 */
export function kodiakTlaSkuShortLabel(fullSku: string): string {
  const trimmed = fullSku.trim();
  let conversionPrefix = "";
  let remainder = trimmed;
  const babaMatch = remainder.match(/^(BABA-)/i);
  if (babaMatch?.[1] != null) {
    conversionPrefix = "BABA-";
    remainder = remainder.slice(babaMatch[1].length);
  } else {
    const abbMatch = remainder.match(/^(ABB-)/i);
    if (abbMatch?.[1] != null) {
      conversionPrefix = "ABB-";
      remainder = remainder.slice(abbMatch[1].length);
    }
  }
  const normalized = stripAltTlaSkuPrefixForDisplay(remainder);
  const fi5a = normalized.match(/FI-5A-([NW])-TLA/i);
  if (fi5a?.[1] != null) {
    return `${conversionPrefix}${fi5a[1].toUpperCase()}-TLA`;
  }
  const fi3c = normalized.match(/-([NBQ])-TLA-([AC])/i);
  if (fi3c?.[1] != null && fi3c[2] != null) {
    return `${conversionPrefix}${fi3c[1].toUpperCase()}-TLA-${fi3c[2].toUpperCase()}`;
  }
  return conversionPrefix === "" ? normalized : `${conversionPrefix}${normalized}`;
}

export function kodiakTlaSkusEquivalent(left: string, right: string): boolean {
  return kodiakTlaSkuShortLabel(left) === kodiakTlaSkuShortLabel(right);
}

export function kodiakPlannedLockIsTlaSpecific(planned: KodiakPlannedPrefix): boolean {
  return planned.tla_build_order_id != null && Number(planned.tla_build_order_id) > 0;
}

export function kodiakPlannedLockMatchesTlaRow(
  planned: KodiakPlannedPrefix,
  tlaBuildOrderId: number,
  tlaSku: string,
): boolean {
  if (
    planned.tla_build_order_id != null &&
    Number(planned.tla_build_order_id) === Number(tlaBuildOrderId)
  ) {
    return true;
  }
  const allowed = (planned.allowed_tla_skus ?? []).filter((sku) => sku.trim() !== "");
  if (allowed.length === 1) {
    return kodiakTlaSkusEquivalent(allowed[0] ?? "", tlaSku);
  }
  return false;
}

export function kodiakParentRowPrefixAssignments(
  assignments: KodiakPcbaPrefixAssignment[],
  planned_by_variant: Map<string, KodiakPlannedPrefix>,
): KodiakPcbaPrefixAssignment[] {
  return assignments.filter((assignment) => {
    const planned = planned_by_variant.get(
      kodiakPrefixVariantKey(assignment.pcba_prefix, assignment.core_feature),
    );
    if (planned == null) {
      return true;
    }
    return !kodiakPlannedLockIsTlaSpecific(planned);
  });
}

/**
 * Prefix chips for the PCBA prefix column.
 * A grouped parent hides TLA-locked chips because each TLA child row shows its own.
 * A single flat row has no child, so the locked prefix must stay in this column.
 */
export function kodiakPcbaColumnPrefixAssignments(
  pcbaBuildOrderId: number,
  row: { tla_build_order_id: number | null; tla_sku: string | null },
  groupIsSplit: boolean,
  prefixViewModel: KodiakPrefixAssignmentViewModel,
): KodiakPcbaPrefixAssignment[] {
  const allAssignments =
    prefixViewModel.chips_by_build_order_id.get(Number(pcbaBuildOrderId)) ?? [];
  const parentAssignments = kodiakParentRowPrefixAssignments(
    allAssignments,
    prefixViewModel.planned_by_variant,
  );
  if (groupIsSplit || row.tla_build_order_id == null || row.tla_sku == null || row.tla_sku.trim() === "") {
    return parentAssignments;
  }
  const tlaAssignments = kodiakTlaRowPrefixAssignments(
    pcbaBuildOrderId,
    Number(row.tla_build_order_id),
    row.tla_sku,
    prefixViewModel,
  );
  const seen = new Set(parentAssignments.map((assignment) => kodiakPrefixVariantKey(assignment.pcba_prefix, assignment.core_feature)));
  const merged = [...parentAssignments];
  for (const assignment of tlaAssignments) {
    const key = kodiakPrefixVariantKey(assignment.pcba_prefix, assignment.core_feature);
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(assignment);
  }
  return merged;
}

export function kodiakTlaRowPrefixAssignments(
  pcbaBuildOrderId: number,
  tlaBuildOrderId: number,
  tlaSku: string,
  prefixViewModel: KodiakPrefixAssignmentViewModel,
): KodiakPcbaPrefixAssignment[] {
  const assignments = prefixViewModel.chips_by_build_order_id.get(Number(pcbaBuildOrderId)) ?? [];
  return assignments.filter((assignment) => {
    const planned = prefixViewModel.planned_by_variant.get(
      kodiakPrefixVariantKey(assignment.pcba_prefix, assignment.core_feature),
    );
    if (planned == null) {
      return false;
    }
    return kodiakPlannedLockMatchesTlaRow(planned, tlaBuildOrderId, tlaSku);
  });
}

export function resolveKodiakPrefixTlaShortLabels(
  prefix: string,
  coreFeature: string,
  viewModel: KodiakPrefixAssignmentViewModel,
): Set<string> {
  const labels = new Set<string>();
  const addLabel = (tlaSku: string): void => {
    const trimmed = tlaSku.trim();
    if (trimmed === "" || trimmed === "—") return;
    if (trimmed.endsWith("-TLA-?")) {
      labels.add(trimmed);
      return;
    }
    labels.add(kodiakTlaSkuShortLabel(trimmed));
  };

  const variantKey = kodiakPrefixVariantKey(prefix, coreFeature);
  for (const hover of viewModel.hover_by_assignment_key.values()) {
    if (hover.pcba_prefix !== prefix) continue;
    for (const row of hover.rows) {
      addLabel(row.tla_sku);
    }
  }
  for (const row of viewModel.discovered_hover_by_variant.get(variantKey) ?? []) {
    addLabel(row.tla_sku);
  }
  return labels;
}

export function kodiakPrefixTlaLabelsMatchTargetGroup(
  prefix: string,
  coreFeature: string,
  viewModel: KodiakPrefixAssignmentViewModel,
  targetTlaShortLabels: string[],
  targetCoreFeature: string | null,
): boolean {
  if (coreFeature.trim().toUpperCase() === "?") {
    return targetCoreFeature != null;
  }
  const requiredLabels = resolveKodiakPrefixTlaShortLabels(prefix, coreFeature, viewModel);
  if (requiredLabels.size === 0) return true;

  const normalizedTargets = targetTlaShortLabels.map((label) => {
    const trimmed = label.trim();
    if (trimmed === "" || trimmed === "—") return trimmed;
    if (trimmed.endsWith("-TLA-?")) return trimmed;
    return kodiakTlaSkuShortLabel(trimmed);
  });

  for (const label of normalizedTargets) {
    if (label === "" || label === "—") continue;
    if (requiredLabels.has(label)) return true;
  }
  if (targetCoreFeature != null && requiredLabels.has(`${targetCoreFeature}-TLA-?`)) {
    return true;
  }
  return false;
}

export function parseKodiakPurchaseOrderId(value: unknown): number | null {
  if (typeof value === "number" && Number.isInteger(value) && value > 0) {
    return value;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!/^\d+$/.test(trimmed)) return null;
    const parsed = Number.parseInt(trimmed, 10);
    return parsed > 0 ? parsed : null;
  }
  return null;
}

export function resolveKodiakPrefixDragPurchaseOrderId(
  payload: KodiakPrefixDragPayload,
  viewModel: KodiakPrefixAssignmentViewModel,
): number | null {
  const direct = parseKodiakPurchaseOrderId(payload.purchase_order_id);
  if (direct != null) return direct;
  if (payload.from_pcba_build_order_id == null) return null;
  const fromBuildOrderId = Number(payload.from_pcba_build_order_id);
  for (const bucket of viewModel.chips_by_build_order_id.values()) {
    for (const assignment of bucket) {
      if (
        assignment.pcba_prefix === payload.pcba_prefix &&
        assignment.core_feature === payload.core_feature &&
        Number(assignment.pcba_build_order_id) === fromBuildOrderId
      ) {
        return parseKodiakPurchaseOrderId(assignment.purchase_order_id);
      }
    }
  }
  return null;
}

export function kodiakPrefixCanAssignToPcbaCoreFeature(
  prefix: string,
  coreFeature: string,
  payload: KodiakPrefixDragPayload,
  targetCoreFeature: string | null,
  viewModel: KodiakPrefixAssignmentViewModel,
): { ok: true } | { ok: false; reason: string } {
  if (targetCoreFeature == null) {
    return { ok: false, reason: "Target PCBA row has no N, Q, or B feature." };
  }
  const prefixCoreFeature = resolveKodiakPrefixCoreFeature(prefix, coreFeature, payload, viewModel);
  if (prefixCoreFeature == null) {
    return {
      ok: false,
      reason: `Prefix ${prefix}:${coreFeature} has no assignable core feature.`,
    };
  }
  if (prefixCoreFeature === "?") {
    if (targetCoreFeature == null) {
      return { ok: false, reason: "Target PCBA row has no N, Q, or B feature." };
    }
    return { ok: true };
  }
  if (prefixCoreFeature !== targetCoreFeature) {
    return {
      ok: false,
      reason: `Prefix ${prefix}:${prefixCoreFeature} cannot assign to ${targetCoreFeature} PCBA row.`,
    };
  }
  return { ok: true };
}

export interface KodiakTlaPrefixDropTarget {
  purchase_order_id: number;
  pcba_build_order_id: number;
  tla_build_order_id: number;
  tla_sku: string;
  tla_work_order_number: string | null;
  target_core_feature: string | null;
}

export function kodiakPrefixCanDropOnTlaTarget(
  payload: KodiakPrefixDragPayload,
  target: {
    purchase_order_id: number;
    pcba_build_order_id: number;
    target_tla_sku: string;
    target_core_feature: string | null;
  },
  viewModel: KodiakPrefixAssignmentViewModel,
): boolean {
  // TLA sub-rows share the parent PCBA build order — dragging from the parent chip
  // zone to a child row is the primary path for a single-TLA planned lock.
  if (
    !kodiakPrefixCanAssignToPcbaCoreFeature(
      payload.pcba_prefix,
      payload.core_feature,
      payload,
      target.target_core_feature,
      viewModel,
    ).ok
  ) {
    return false;
  }
  const trimmedTlaSku = target.target_tla_sku.trim();
  const targetTlaShortLabels =
    trimmedTlaSku === "" || trimmedTlaSku === "—" ? [] : [kodiakTlaSkuShortLabel(trimmedTlaSku)];
  return kodiakPrefixTlaLabelsMatchTargetGroup(
    payload.pcba_prefix,
    payload.core_feature,
    viewModel,
    targetTlaShortLabels,
    target.target_core_feature,
  );
}

export function kodiakPrefixCanDropOnPcbaTarget(
  payload: KodiakPrefixDragPayload,
  target: {
    purchase_order_id: number;
    pcba_build_order_id: number;
    target_core_feature: string | null;
    target_tla_short_labels?: string[];
  },
  viewModel: KodiakPrefixAssignmentViewModel,
): boolean {
  if (
    payload.from_pcba_build_order_id != null &&
    Number(payload.from_pcba_build_order_id) === Number(target.pcba_build_order_id)
  ) {
    return false;
  }
  if (
    !kodiakPrefixCanAssignToPcbaCoreFeature(
      payload.pcba_prefix,
      payload.core_feature,
      payload,
      target.target_core_feature,
      viewModel,
    ).ok
  ) {
    return false;
  }
  return kodiakPrefixTlaLabelsMatchTargetGroup(
    payload.pcba_prefix,
    payload.core_feature,
    viewModel,
    target.target_tla_short_labels ?? [],
    target.target_core_feature,
  );
}

export function kodiakPrefixCanDropOnUnallocatedTarget(payload: KodiakPrefixDragPayload | null): boolean {
  return payload != null && payload.from_pcba_build_order_id != null;
}

const KODIAK_PREFIX_DROP_ELIGIBLE_CLASS =
  "ring-1 ring-dashed ring-brand-300/80 bg-brand-50/70";
const KODIAK_PREFIX_DROP_HOVER_CLASS = "ring-2 ring-brand-400 bg-brand-100/80";

export function kodiakPrefixAssignmentHoverKey(
  purchaseOrderId: number | string,
  pcbaBuildOrderId: number | string,
  pcbaPrefix: string,
  coreFeature: string,
): string {
  return `${Number(purchaseOrderId)}:${Number(pcbaBuildOrderId)}:${pcbaPrefix}:${coreFeature}`;
}

/** Assigned chips use PO-scoped assignment hover when available; unallocated chips use discovered rows. */
export function resolveKodiakPrefixChipHoverDetail(
  prefix: string,
  coreFeature: string,
  assignment: { purchase_order_id: number; pcba_build_order_id: number } | null,
  discovered_hover_by_variant: Map<string, KodiakPrefixAssignmentHoverRow[]>,
  assignmentHoverDetail?: KodiakPrefixAssignmentHoverDetail,
  planned?: KodiakPlannedPrefix | null,
): KodiakPrefixAssignmentHoverDetail | undefined {
  if (kodiakPrefixHoverHasDisplayableRows(assignmentHoverDetail)) {
    return assignmentHoverDetail;
  }

  const variantKey = kodiakPrefixVariantKey(prefix, coreFeature);
  const discoveredRows = (discovered_hover_by_variant.get(variantKey) ?? []).filter(
    (row) => row.serial_fci_count > 0 || row.qc_incident_count > 0,
  );
  if (discoveredRows.length > 0) {
    return {
      purchase_order_id: assignment?.purchase_order_id ?? 0,
      pcba_build_order_id: assignment?.pcba_build_order_id ?? 0,
      pcba_prefix: prefix,
      rows: discoveredRows,
    };
  }

  if (planned != null) {
    return buildKodiakPlannedPrefixHoverDetail(planned);
  }

  return assignmentHoverDetail;
}

function ensureDiscoveredPrefixEntry(
  discovered_by_variant: Map<string, KodiakDiscoveredPrefix>,
  prefix: string,
  coreFeature: string | null,
): void {
  if (coreFeature == null || coreFeature.trim() === "") return;
  const variantKey = kodiakPrefixVariantKey(prefix, coreFeature);
  if (discovered_by_variant.has(variantKey)) return;
  discovered_by_variant.set(variantKey, {
    prefix,
    serial_fci_count: 0,
    qc_incident_count: 0,
    serial_begin_date: null,
    serial_end_date: null,
    core_feature: coreFeature,
  });
}

export function kodiakPrefixHoverHasDisplayableRows(
  hoverDetail: KodiakPrefixAssignmentHoverDetail | undefined,
): boolean {
  return (hoverDetail?.rows ?? []).some(
    (row) => row.serial_fci_count > 0 || row.qc_incident_count > 0,
  );
}

export function applyKodiakPrefixAssignmentPatch(
  board: KodiakPrefixAssignmentBoard,
  patch: {
    purchase_order_id: number;
    pcba_prefix: string;
    core_feature: string;
    pcba_build_order_id: number | null;
    tla_build_order_id?: number | null;
    tla_sku?: string | null;
    tla_work_order_number?: string | null;
  },
): KodiakPrefixAssignmentBoard {
  const purchaseOrderId = Number(patch.purchase_order_id);
  const pcbaPrefix = patch.pcba_prefix.trim();
  const coreFeature = patch.core_feature.trim().toUpperCase();
  const assignments = board.assignments.filter(
    (assignment) =>
      !(
        assignment.pcba_prefix === pcbaPrefix &&
        assignment.core_feature === coreFeature
      ),
  );
  const assignment_hover = board.assignment_hover.filter(
    (hover) => !(hover.pcba_prefix === pcbaPrefix),
  );

  if (patch.pcba_build_order_id == null) {
    const removedAssignment = board.assignments.find(
      (assignment) =>
        assignment.pcba_prefix === pcbaPrefix && assignment.core_feature === coreFeature,
    );
    const hasDiscovered = board.discovered_prefixes.some(
      (entry) => entry.prefix === pcbaPrefix && entry.core_feature === coreFeature,
    );
    let discovered_prefixes = board.discovered_prefixes;
    if (!hasDiscovered && removedAssignment != null) {
      const target = board.pcba_targets.find(
        (entry) => Number(entry.pcba_build_order_id) === Number(removedAssignment.pcba_build_order_id),
      );
      discovered_prefixes = [
        ...board.discovered_prefixes,
        {
          prefix: pcbaPrefix,
          serial_fci_count: 0,
          qc_incident_count: 0,
          serial_begin_date: null,
          serial_end_date: null,
          core_feature:
            removedAssignment.core_feature ??
            target?.core_feature ??
            (target?.pcba_sku == null ? null : kodiakPcbaSkuCoreFeature(target.pcba_sku)),
        },
      ];
    }
    return removeKodiakPlannedPrefixPatch(
      { ...board, assignments, assignment_hover, discovered_prefixes },
      pcbaPrefix,
      coreFeature,
    );
  }

  const pcbaBuildOrderId = Number(patch.pcba_build_order_id);
  const target = board.pcba_targets.find(
    (entry) =>
      Number(entry.purchase_order_id) === purchaseOrderId &&
      Number(entry.pcba_build_order_id) === pcbaBuildOrderId,
  );
  if (target == null) {
    return { ...board, assignments, assignment_hover };
  }

  const assignmentsWithoutVariant = assignments.filter(
    (assignment) =>
      !(assignment.pcba_prefix === pcbaPrefix && assignment.core_feature === coreFeature),
  );
  const assignment_hover_without_variant = assignment_hover.filter(
    (hover) => hover.pcba_prefix !== pcbaPrefix,
  );

  const nextBoard: KodiakPrefixAssignmentBoard = {
    ...board,
    assignments: [
      ...assignmentsWithoutVariant,
      {
        purchase_order_id: purchaseOrderId,
        po_number: target.po_number,
        pcba_prefix: pcbaPrefix,
        core_feature: coreFeature,
        pcba_build_order_id: pcbaBuildOrderId,
        pcba_work_order_number: target.pcba_work_order_number,
      },
    ],
    assignment_hover: assignment_hover_without_variant,
  };

  const tlaBuildOrderId =
    patch.tla_build_order_id == null ? null : Number(patch.tla_build_order_id);
  if (tlaBuildOrderId == null || tlaBuildOrderId <= 0) {
    return nextBoard;
  }

  const tlaSku = patch.tla_sku == null ? "" : patch.tla_sku.trim();
  return applyKodiakPlannedPrefixPatch(nextBoard, {
    pcba_prefix: pcbaPrefix,
    core_feature: coreFeature,
    expected_tla_sku: tlaSku !== "" ? tlaSku : `${coreFeature}-TLA-?`,
    purchase_order_id: purchaseOrderId,
    pcba_build_order_id: pcbaBuildOrderId,
    tla_build_order_id: tlaBuildOrderId,
    tla_work_order_number:
      patch.tla_work_order_number == null || patch.tla_work_order_number.trim() === ""
        ? null
        : patch.tla_work_order_number,
    po_number: target.po_number,
    notes: null,
    allowed_tla_skus: tlaSku !== "" ? [tlaSku] : null,
  });
}

export function buildKodiakPrefixAssignmentViewModel(
  board: KodiakPrefixAssignmentBoard | undefined,
): KodiakPrefixAssignmentViewModel {
  const discovered_by_variant = new Map<string, KodiakDiscoveredPrefix>();
  const core_feature_by_variant = new Map<string, string | null>();
  for (const entry of board?.discovered_prefixes ?? []) {
    const coreFeature = entry.core_feature ?? "?";
    const variantKey = kodiakPrefixVariantKey(entry.prefix, coreFeature);
    discovered_by_variant.set(variantKey, entry);
    core_feature_by_variant.set(variantKey, entry.core_feature ?? null);
  }

  const pcba_core_feature_by_build_order_id = new Map<number, string | null>();
  for (const target of board?.pcba_targets ?? []) {
    pcba_core_feature_by_build_order_id.set(
      Number(target.pcba_build_order_id),
      target.core_feature ?? kodiakPcbaSkuCoreFeature(target.pcba_sku),
    );
  }

  const assigned_variants = new Set<string>();
  const chips_by_build_order_id = new Map<number, KodiakPcbaPrefixAssignment[]>();
  const hover_by_assignment_key = new Map<string, KodiakPrefixAssignmentHoverDetail>();
  for (const assignment of board?.assignments ?? []) {
    const purchaseOrderId = Number(assignment.purchase_order_id);
    const pcbaBuildOrderId = Number(assignment.pcba_build_order_id);
    const coreFeature = assignment.core_feature;
    const variantKey = kodiakPrefixVariantKey(assignment.pcba_prefix, coreFeature);
    assigned_variants.add(variantKey);
    ensureDiscoveredPrefixEntry(discovered_by_variant, assignment.pcba_prefix, coreFeature);
    core_feature_by_variant.set(variantKey, coreFeature);
    const bucket = chips_by_build_order_id.get(pcbaBuildOrderId) ?? [];
    bucket.push({
      ...assignment,
      purchase_order_id: purchaseOrderId,
      pcba_build_order_id: pcbaBuildOrderId,
    });
    chips_by_build_order_id.set(pcbaBuildOrderId, bucket);
  }
  for (const hover of board?.discovered_prefix_hover ?? []) {
    ensureDiscoveredPrefixEntry(
      discovered_by_variant,
      hover.pcba_prefix,
      hover.core_feature,
    );
  }

  const planned_by_variant = new Map<string, KodiakPlannedPrefix>();
  for (const planned of board?.planned_prefixes ?? []) {
    const variantKey = kodiakPrefixVariantKey(planned.pcba_prefix, planned.core_feature);
    planned_by_variant.set(variantKey, planned);
    ensureDiscoveredPrefixEntry(discovered_by_variant, planned.pcba_prefix, planned.core_feature);
    core_feature_by_variant.set(variantKey, planned.core_feature);
  }

  for (const hover of board?.assignment_hover ?? []) {
    const purchaseOrderId = Number(hover.purchase_order_id);
    const pcbaBuildOrderId = Number(hover.pcba_build_order_id);
    const matchingAssignment = board?.assignments.find(
      (assignment) =>
        Number(assignment.purchase_order_id) === purchaseOrderId &&
        Number(assignment.pcba_build_order_id) === pcbaBuildOrderId &&
        assignment.pcba_prefix === hover.pcba_prefix,
    );
    const coreFeature = matchingAssignment?.core_feature ?? "?";
    hover_by_assignment_key.set(
      kodiakPrefixAssignmentHoverKey(purchaseOrderId, pcbaBuildOrderId, hover.pcba_prefix, coreFeature),
      {
        purchase_order_id: purchaseOrderId,
        pcba_build_order_id: pcbaBuildOrderId,
        pcba_prefix: hover.pcba_prefix,
        rows: Array.isArray(hover.rows) ? hover.rows : [],
        rewire_note: hover.rewire_note ?? null,
      },
    );
  }
  for (const bucket of chips_by_build_order_id.values()) {
    bucket.sort((left, right) => {
      const prefixCompare = left.pcba_prefix.localeCompare(right.pcba_prefix);
      if (prefixCompare !== 0) return prefixCompare;
      return left.core_feature.localeCompare(right.core_feature);
    });
  }

  const unallocated_prefixes = [...discovered_by_variant.values()]
    .filter((entry) => {
      const coreFeature = entry.core_feature ?? "?";
      return !assigned_variants.has(kodiakPrefixVariantKey(entry.prefix, coreFeature));
    })
    .sort((left, right) => {
      const prefixCompare = left.prefix.localeCompare(right.prefix);
      if (prefixCompare !== 0) return prefixCompare;
      const leftCore = String(left.core_feature ?? "");
      const rightCore = String(right.core_feature ?? "");
      if (leftCore === "?" && rightCore !== "?") return 1;
      if (rightCore === "?" && leftCore !== "?") return -1;
      return leftCore.localeCompare(rightCore);
    });

  const discovered_hover_by_variant = new Map<string, KodiakPrefixAssignmentHoverRow[]>();
  for (const hover of board?.discovered_prefix_hover ?? []) {
    const variantKey = kodiakPrefixVariantKey(hover.pcba_prefix, hover.core_feature);
    discovered_hover_by_variant.set(variantKey, Array.isArray(hover.rows) ? hover.rows : []);
  }

  return {
    discovered_by_variant,
    discovered_hover_by_variant,
    planned_by_variant,
    unallocated_prefixes,
    chips_by_build_order_id,
    hover_by_assignment_key,
    core_feature_by_variant,
    pcba_core_feature_by_build_order_id,
  };
}

export function applyKodiakPlannedPrefixPatch(
  board: KodiakPrefixAssignmentBoard,
  patch: KodiakPlannedPrefix,
): KodiakPrefixAssignmentBoard {
  const planned_prefixes = [
    ...(board.planned_prefixes ?? []).filter(
      (row) =>
        !(
          row.pcba_prefix === patch.pcba_prefix &&
          row.core_feature === patch.core_feature
        ),
    ),
    patch,
  ];
  return { ...board, planned_prefixes };
}

export function removeKodiakPlannedPrefixPatch(
  board: KodiakPrefixAssignmentBoard,
  pcbaPrefix: string,
  coreFeature: string,
): KodiakPrefixAssignmentBoard {
  return {
    ...board,
    planned_prefixes: (board.planned_prefixes ?? []).filter(
      (row) => !(row.pcba_prefix === pcbaPrefix && row.core_feature === coreFeature),
    ),
  };
}

export function buildKodiakPlannedPrefixHoverDetail(
  planned: KodiakPlannedPrefix,
): KodiakPrefixAssignmentHoverDetail {
  const allowed = (planned.allowed_tla_skus ?? []).filter((sku) => sku.trim() !== "");
  const tlaLabel =
    allowed.length > 1
      ? [...new Set(allowed.map((sku) => kodiakTlaSkuShortLabel(sku)))].sort().join(" or ")
      : planned.tla_work_order_number != null && planned.tla_work_order_number.trim() !== ""
        ? `${planned.tla_work_order_number} · ${kodiakTlaSkuShortLabel(planned.expected_tla_sku)}`
        : kodiakTlaSkuShortLabel(planned.expected_tla_sku);
  const rewireParts = [
    planned.po_number != null && planned.po_number.trim() !== "" ? `PO ${planned.po_number}` : null,
    planned.notes,
  ].filter((part): part is string => part != null && part.trim() !== "");
  return {
    purchase_order_id: planned.purchase_order_id ?? 0,
    pcba_build_order_id: planned.pcba_build_order_id ?? 0,
    pcba_prefix: planned.pcba_prefix,
    rows: [
      {
        pcba_sku: "—",
        tla_sku: tlaLabel,
        serial_fci_count: 0,
        qc_incident_count: 0,
        serial_begin_date: null,
        serial_end_date: null,
      },
    ],
    rewire_note: rewireParts.length > 0 ? rewireParts.join(" · ") : null,
  };
}

function formatNumber(value: number): string {
  return value.toLocaleString("en-US");
}

function KodiakPrefixHoverTable({
  headers,
  rows,
  rightAlignedColumnIndexes = [],
}: {
  headers: string[];
  rows: Array<Array<string | number>>;
  rightAlignedColumnIndexes?: number[];
}): JSX.Element | null {
  if (rows.length === 0) return null;
  return (
    <table className="min-w-[28rem] border-collapse text-[11px] leading-snug">
      <thead>
        <tr className="text-slate-500">
          {headers.map((header, headerIndex) => (
            <th
              key={`${header}-${headerIndex}`}
              className={cn(
                "px-1.5 py-0.5 font-medium first:pl-0 last:pr-0",
                rightAlignedColumnIndexes.includes(headerIndex) ? "text-right" : "text-left",
              )}
            >
              {header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((cells, rowIndex) => (
          <tr key={rowIndex} className="border-t border-slate-100 text-slate-800">
            {cells.map((cell, cellIndex) => (
              <td
                key={cellIndex}
                className={cn(
                  "px-1.5 py-0.5 first:pl-0 last:pr-0",
                  rightAlignedColumnIndexes.includes(cellIndex) ? "text-right tabular-nums" : "text-left",
                )}
              >
                {typeof cell === "number" ? formatNumber(cell) : cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function KodiakPrefixHoverBody({
  discovered,
  hoverDetail,
}: {
  discovered: KodiakDiscoveredPrefix | undefined;
  hoverDetail: KodiakPrefixAssignmentHoverDetail | undefined;
}): JSX.Element {
  const tableRows = (hoverDetail?.rows ?? []).filter(
    (row) => row.serial_fci_count > 0 || row.qc_incident_count > 0,
  );

  if (tableRows.length === 0) {
    const coreFeature = discovered?.core_feature;
    const inferredPcbaSku =
      coreFeature == null
        ? "—"
        : coreFeature === "W"
          ? "15-1485-W-ID2-R17"
          : `15-1607-${coreFeature}-ID2-R6`;
    const inferredTlaSku =
      coreFeature == null ? "—" : coreFeature === "W" ? "W-TLA" : `${coreFeature}-TLA-?`;
    return (
      <KodiakPrefixHoverTable
        headers={[...KODIAK_PREFIX_HOVER_HEADERS]}
        rows={[
          [
            inferredPcbaSku,
            inferredTlaSku,
            discovered?.serial_fci_count ?? 0,
            discovered?.qc_incident_count ?? 0,
            formatTlaPlanningDate(discovered?.serial_begin_date ?? null),
            formatTlaPlanningDate(discovered?.serial_end_date ?? null),
          ],
        ]}
        rightAlignedColumnIndexes={[2, 3]}
      />
    );
  }

  return (
    <div className="space-y-2">
      <KodiakPrefixHoverTable
        headers={[...KODIAK_PREFIX_HOVER_HEADERS]}
        rows={tableRows.map((row) => [
          row.pcba_sku,
          stripAltTlaSkuPrefixForDisplay(row.tla_sku),
          row.serial_fci_count,
          row.qc_incident_count,
          formatTlaPlanningDate(row.serial_begin_date),
          formatTlaPlanningDate(row.serial_end_date),
        ])}
        rightAlignedColumnIndexes={[2, 3]}
      />
      {hoverDetail?.rewire_note ? (
        <p className="max-w-md text-xs leading-snug text-amber-800">{hoverDetail.rewire_note}</p>
      ) : null}
    </div>
  );
}

export function KodiakPrefixChip({
  prefix,
  coreFeature,
  discovered,
  hoverDetail,
  discoveryHoverDetail,
  planned,
  draggable,
  onDragStart,
}: {
  prefix: string;
  coreFeature: string;
  discovered: KodiakDiscoveredPrefix | undefined;
  hoverDetail?: KodiakPrefixAssignmentHoverDetail;
  /** Raw serial/QC rows for chip color and misallocation — never filtered by lock. */
  discoveryHoverDetail?: KodiakPrefixAssignmentHoverDetail;
  planned?: KodiakPlannedPrefix | null;
  draggable: boolean;
  onDragStart: (event: ReactDragEvent<HTMLSpanElement>) => void;
}): JSX.Element {
  const discoveryHover =
    discoveryHoverDetail != null && kodiakPrefixHoverHasDisplayableRows(discoveryHoverDetail)
      ? discoveryHoverDetail
      : hoverDetail;
  const chipAppearance = resolveKodiakPrefixChipAppearanceFromDiscovery(
    discovered,
    discoveryHover,
    planned,
  );

  return (
    <HoverHintContent
      hoverHint={<KodiakPrefixHoverBody discovered={discovered} hoverDetail={hoverDetail} />}
      align="start"
    >
      <span
        draggable={draggable}
        onDragStart={onDragStart}
        onDragEnd={clearActiveKodiakPrefixDragPayloadDeferred}
        className={cn(draggable ? "cursor-grab active:cursor-grabbing" : "cursor-default opacity-70")}
      >
        <KodiakFamilyChip label={kodiakPrefixVariantLabel(prefix, coreFeature)} appearance={chipAppearance} />
      </span>
    </HoverHintContent>
  );
}

const KODIAK_UNALLOCATED_PREFIXES_HOVER_HINT =
  "Drag a prefix variant (for example 46550:N or 46550:B) onto a PCBA row to lock it to that PCBA's TLA lines, or onto a specific TLA sub-row to lock to one TLA. Drag back here to unassign. Use Add to register a prefix before serial activity exists. QC-only unknown variants (46550:?) can assign to any matching N/Q/B PCBA row you choose.";

export function KodiakUnallocatedPrefixCard({
  prefixes,
  discovered_by_variant,
  discovered_hover_by_variant,
  planned_by_variant,
  disabled,
  adding,
  onAddPrefix,
  onDropUnallocate,
  onDragStartPrefix,
}: {
  prefixes: KodiakDiscoveredPrefix[];
  discovered_by_variant: Map<string, KodiakDiscoveredPrefix>;
  discovered_hover_by_variant: Map<string, KodiakPrefixAssignmentHoverRow[]>;
  planned_by_variant: Map<string, KodiakPlannedPrefix>;
  disabled: boolean;
  adding: boolean;
  onAddPrefix: (pcbaPrefix: string) => void;
  onDropUnallocate: (payload: KodiakPrefixDragPayload) => void;
  onDragStartPrefix: (event: ReactDragEvent<HTMLSpanElement>, payload: KodiakPrefixDragPayload) => void;
}): JSX.Element {
  const [dragOver, setDragOver] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addDraft, setAddDraft] = useState("");
  const activeDrag = useActiveKodiakPrefixDragPayload();
  const isEligibleUnallocateTarget = kodiakPrefixCanDropOnUnallocatedTarget(activeDrag);

  const handleAddSubmit = (): void => {
    const trimmed = addDraft.trim();
    if (!/^[0-9]{5}$/.test(trimmed)) {
      return;
    }
    onAddPrefix(trimmed);
    setAddDraft("");
    setAddOpen(false);
  };

  const handleDragOver = useCallback(
    (event: ReactDragEvent<HTMLDivElement>) => {
      if (disabled || !isEligibleUnallocateTarget) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
      setDragOver(true);
    },
    [disabled, isEligibleUnallocateTarget],
  );

  const handleDrop = useCallback(
    (event: ReactDragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setDragOver(false);
      if (disabled) return;
      const payload = parseKodiakPrefixDragPayloadFromDropEvent(event);
      if (payload == null || payload.from_pcba_build_order_id == null) return;
      const activeDrag = getActiveKodiakPrefixDragPayload();
      const mergedPayload =
        activeDrag != null &&
        activeDrag.pcba_prefix === payload.pcba_prefix &&
        activeDrag.core_feature === payload.core_feature &&
        Number(activeDrag.from_pcba_build_order_id) === Number(payload.from_pcba_build_order_id)
          ? { ...activeDrag, ...payload }
          : payload;
      onDropUnallocate(mergedPayload);
    },
    [disabled, onDropUnallocate],
  );

  return (
    <Card className="border-dashed border-slate-300 bg-slate-50/80">
      <CardHeader className="flex flex-row items-center justify-between gap-2 px-3 py-2">
        <CardTitleWithHelp
          hoverHint={KODIAK_UNALLOCATED_PREFIXES_HOVER_HINT}
          titleClassName="text-sm font-semibold text-slate-800"
        >
          Unallocated prefixes
        </CardTitleWithHelp>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          disabled={disabled || adding}
          isLoading={adding}
          onClick={() => setAddOpen((previous) => !previous)}
        >
          Add
        </Button>
      </CardHeader>
      <CardBody className="px-3 pb-3 pt-0">
        {addOpen ? (
          <div className="mb-3 flex flex-wrap items-end gap-2">
            <div className="min-w-[8rem] flex-1">
              <label className="block text-xs font-medium text-slate-700" htmlFor="kodiak-manual-prefix">
                PCBA prefix
              </label>
              <Input
                id="kodiak-manual-prefix"
                value={addDraft}
                onChange={(event) => setAddDraft(event.target.value.replace(/\D/g, "").slice(0, 5))}
                placeholder="46550"
                disabled={disabled || adding}
                className="mt-1 font-mono"
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    handleAddSubmit();
                  }
                }}
              />
            </div>
            <Button
              type="button"
              size="sm"
              disabled={disabled || adding || !/^[0-9]{5}$/.test(addDraft.trim())}
              isLoading={adding}
              onClick={handleAddSubmit}
            >
              Register
            </Button>
          </div>
        ) : null}
        <div
          onDragOver={handleDragOver}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          className={cn(
            "flex min-h-10 flex-wrap gap-1.5 rounded-md border border-dashed p-2 transition-colors",
            dragOver && isEligibleUnallocateTarget
              ? cn("border-brand-400", KODIAK_PREFIX_DROP_HOVER_CLASS)
              : isEligibleUnallocateTarget
                ? cn("border-brand-300", KODIAK_PREFIX_DROP_ELIGIBLE_CLASS)
                : "border-slate-200 bg-white",
          )}
        >
          {prefixes.length === 0 ? (
            <span className="text-xs text-slate-400">No unassigned prefixes — drop here to unallocate.</span>
          ) : (
            prefixes.map((entry) => {
              const coreFeature = entry.core_feature ?? "?";
              const variantKey = kodiakPrefixVariantKey(entry.prefix, coreFeature);
              const planned = planned_by_variant.get(variantKey);
              return (
              <KodiakPrefixChip
                key={variantKey}
                prefix={entry.prefix}
                coreFeature={coreFeature}
                discovered={discovered_by_variant.get(variantKey) ?? entry}
                hoverDetail={
                  resolveKodiakPrefixChipHoverDetail(
                    entry.prefix,
                    coreFeature,
                    null,
                    discovered_hover_by_variant,
                    undefined,
                    planned,
                  )
                }
                draggable={!disabled}
                onDragStart={(event) =>
                  onDragStartPrefix(event, {
                    purchase_order_id: null,
                    pcba_prefix: entry.prefix,
                    core_feature: coreFeature,
                    from_pcba_build_order_id: null,
                  })
                }
              />
              );
            })
          )}
        </div>
      </CardBody>
    </Card>
  );
}

export function KodiakAssignedPrefixChips({
  assignments,
  discovered_by_variant,
  discovered_hover_by_variant,
  planned_by_variant,
  hover_by_assignment_key,
  disabled,
  onDragStartPrefix,
}: {
  assignments: KodiakPcbaPrefixAssignment[];
  discovered_by_variant: Map<string, KodiakDiscoveredPrefix>;
  discovered_hover_by_variant: Map<string, KodiakPrefixAssignmentHoverRow[]>;
  planned_by_variant: Map<string, KodiakPlannedPrefix>;
  hover_by_assignment_key: Map<string, KodiakPrefixAssignmentHoverDetail>;
  disabled: boolean;
  onDragStartPrefix: (event: ReactDragEvent<HTMLSpanElement>, payload: KodiakPrefixDragPayload) => void;
}): JSX.Element {
  return (
    <>
      {assignments.map((assignment) => {
        const variantKey = kodiakPrefixVariantKey(assignment.pcba_prefix, assignment.core_feature);
        const planned = planned_by_variant.get(variantKey);
        const assignmentHover = hover_by_assignment_key.get(
          kodiakPrefixAssignmentHoverKey(
            assignment.purchase_order_id,
            assignment.pcba_build_order_id,
            assignment.pcba_prefix,
            assignment.core_feature,
          ),
        );
        return (
          <KodiakPrefixChip
            key={`${assignment.purchase_order_id}:${assignment.pcba_prefix}:${assignment.core_feature}`}
            prefix={assignment.pcba_prefix}
            coreFeature={assignment.core_feature}
            discovered={discovered_by_variant.get(variantKey)}
            discoveryHoverDetail={assignmentHover}
            hoverDetail={resolveKodiakPrefixChipHoverDetail(
              assignment.pcba_prefix,
              assignment.core_feature,
              {
                purchase_order_id: Number(assignment.purchase_order_id),
                pcba_build_order_id: Number(assignment.pcba_build_order_id),
              },
              discovered_hover_by_variant,
              assignmentHover,
              planned,
            )}
            planned={planned}
            draggable={!disabled}
            onDragStart={(event) =>
              onDragStartPrefix(event, {
                purchase_order_id: parseKodiakPurchaseOrderId(assignment.purchase_order_id),
                pcba_prefix: assignment.pcba_prefix,
                core_feature: assignment.core_feature,
                from_pcba_build_order_id: Number(assignment.pcba_build_order_id),
              })
            }
          />
        );
      })}
    </>
  );
}

export function KodiakPcbaPrefixChipZone({
  purchaseOrderId,
  pcbaBuildOrderId,
  targetCoreFeature,
  targetTlaShortLabels,
  assignments,
  discovered_by_variant,
  discovered_hover_by_variant,
  planned_by_variant,
  hover_by_assignment_key,
  prefixViewModel,
  disabled,
  onAssignPrefix,
  onDragStartPrefix,
}: {
  purchaseOrderId: number | null;
  pcbaBuildOrderId: number | null;
  targetCoreFeature: string | null;
  targetTlaShortLabels: string[];
  assignments: KodiakPcbaPrefixAssignment[];
  discovered_by_variant: Map<string, KodiakDiscoveredPrefix>;
  discovered_hover_by_variant: Map<string, KodiakPrefixAssignmentHoverRow[]>;
  planned_by_variant: Map<string, KodiakPlannedPrefix>;
  hover_by_assignment_key: Map<string, KodiakPrefixAssignmentHoverDetail>;
  prefixViewModel: KodiakPrefixAssignmentViewModel;
  disabled: boolean;
  onAssignPrefix: (payload: KodiakPrefixDragPayload, target: { purchase_order_id: number; pcba_build_order_id: number }) => void;
  onDragStartPrefix: (event: ReactDragEvent<HTMLSpanElement>, payload: KodiakPrefixDragPayload) => void;
}): JSX.Element {
  const [dragOver, setDragOver] = useState(false);
  const activeDrag = useActiveKodiakPrefixDragPayload();
  const canDropBase = !disabled && purchaseOrderId != null && pcbaBuildOrderId != null;

  const isEligibleDropTarget = useMemo(() => {
    if (activeDrag == null || !canDropBase || purchaseOrderId == null || pcbaBuildOrderId == null) {
      return false;
    }
    return kodiakPrefixCanDropOnPcbaTarget(
      activeDrag,
      {
        purchase_order_id: purchaseOrderId,
        pcba_build_order_id: pcbaBuildOrderId,
        target_core_feature: targetCoreFeature,
        target_tla_short_labels: targetTlaShortLabels,
      },
      prefixViewModel,
    );
  }, [
    activeDrag,
    canDropBase,
    pcbaBuildOrderId,
    prefixViewModel,
    purchaseOrderId,
    targetCoreFeature,
    targetTlaShortLabels,
  ]);

  const handleDragOver = useCallback(
    (event: ReactDragEvent<HTMLDivElement>) => {
      if (!canDropBase || !isEligibleDropTarget) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
      setDragOver(true);
    },
    [canDropBase, isEligibleDropTarget],
  );

  const handleDrop = useCallback(
    (event: ReactDragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setDragOver(false);
      if (!canDropBase || purchaseOrderId == null || pcbaBuildOrderId == null) return;
      const payload = parseKodiakPrefixDragPayloadFromDropEvent(event);
      if (payload == null) return;
      event.stopPropagation();
      onAssignPrefix(payload, {
        purchase_order_id: purchaseOrderId,
        pcba_build_order_id: pcbaBuildOrderId,
      });
    },
    [canDropBase, onAssignPrefix, purchaseOrderId, pcbaBuildOrderId],
  );

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      className={cn(
        "flex min-h-7 flex-wrap gap-1 rounded-md p-0.5 transition-colors",
        dragOver && isEligibleDropTarget
          ? KODIAK_PREFIX_DROP_HOVER_CLASS
          : isEligibleDropTarget
            ? KODIAK_PREFIX_DROP_ELIGIBLE_CLASS
            : undefined,
      )}
    >
      <KodiakAssignedPrefixChips
        assignments={assignments}
        discovered_by_variant={discovered_by_variant}
        discovered_hover_by_variant={discovered_hover_by_variant}
        planned_by_variant={planned_by_variant}
        hover_by_assignment_key={hover_by_assignment_key}
        disabled={disabled}
        onDragStartPrefix={onDragStartPrefix}
      />
    </div>
  );
}

export function useKodiakTlaPrefixDropRow({
  dropTarget,
  prefixViewModel,
  disabled,
  onAssignPrefixToTla,
}: {
  dropTarget: KodiakTlaPrefixDropTarget | null;
  prefixViewModel: KodiakPrefixAssignmentViewModel;
  disabled: boolean;
  onAssignPrefixToTla: (payload: KodiakPrefixDragPayload, target: KodiakTlaPrefixDropTarget) => void;
}): {
  isEligibleDropTarget: boolean;
  rowClassName: string | undefined;
  onDragOver: (event: ReactDragEvent<HTMLTableRowElement>) => void;
  onDragLeave: () => void;
  onDrop: (event: ReactDragEvent<HTMLTableRowElement>) => void;
} {
  const [dragOver, setDragOver] = useState(false);
  const activeDrag = useActiveKodiakPrefixDragPayload();
  const canDropBase =
    !disabled &&
    dropTarget != null &&
    dropTarget.purchase_order_id > 0 &&
    dropTarget.pcba_build_order_id > 0 &&
    dropTarget.tla_build_order_id > 0 &&
    dropTarget.tla_sku.trim() !== "";

  const isEligibleDropTarget = useMemo(() => {
    if (activeDrag == null || !canDropBase || dropTarget == null) {
      return false;
    }
    return kodiakPrefixCanDropOnTlaTarget(
      activeDrag,
      {
        purchase_order_id: dropTarget.purchase_order_id,
        pcba_build_order_id: dropTarget.pcba_build_order_id,
        target_tla_sku: dropTarget.tla_sku,
        target_core_feature: dropTarget.target_core_feature,
      },
      prefixViewModel,
    );
  }, [activeDrag, canDropBase, dropTarget, prefixViewModel]);

  const onDragOver = useCallback(
    (event: ReactDragEvent<HTMLTableRowElement>) => {
      if (!canDropBase || !isEligibleDropTarget) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
      setDragOver(true);
    },
    [canDropBase, isEligibleDropTarget],
  );

  const onDragLeave = useCallback(() => {
    setDragOver(false);
  }, []);

  const onDrop = useCallback(
    (event: ReactDragEvent<HTMLTableRowElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setDragOver(false);
      if (!canDropBase || dropTarget == null) return;
      const payload = parseKodiakPrefixDragPayloadFromDropEvent(event);
      if (payload == null) return;
      if (
        !kodiakPrefixCanDropOnTlaTarget(
          payload,
          {
            purchase_order_id: dropTarget.purchase_order_id,
            pcba_build_order_id: dropTarget.pcba_build_order_id,
            target_tla_sku: dropTarget.tla_sku,
            target_core_feature: dropTarget.target_core_feature,
          },
          prefixViewModel,
        )
      ) {
        return;
      }
      onAssignPrefixToTla(payload, dropTarget);
    },
    [canDropBase, dropTarget, onAssignPrefixToTla, prefixViewModel],
  );

  const rowClassName =
    dragOver && isEligibleDropTarget
      ? KODIAK_PREFIX_DROP_HOVER_CLASS
      : isEligibleDropTarget
        ? KODIAK_PREFIX_DROP_ELIGIBLE_CLASS
        : undefined;

  return { isEligibleDropTarget, rowClassName, onDragOver, onDragLeave, onDrop };
}

export function useKodiakPrefixDragHandlers(): {
  viewModel: (board: KodiakPrefixAssignmentBoard | undefined) => KodiakPrefixAssignmentViewModel;
  startPrefixDrag: (event: ReactDragEvent<HTMLSpanElement>, payload: KodiakPrefixDragPayload) => void;
} {
  const viewModel = useMemo(
    () => (board: KodiakPrefixAssignmentBoard | undefined) => buildKodiakPrefixAssignmentViewModel(board),
    [],
  );

  const startPrefixDrag = useCallback((event: ReactDragEvent<HTMLSpanElement>, payload: KodiakPrefixDragPayload) => {
    event.dataTransfer.setData(KODIAK_PREFIX_DRAG_MIME, JSON.stringify(payload));
    event.dataTransfer.effectAllowed = "move";
  }, []);

  return { viewModel, startPrefixDrag };
}
